import { DateTime } from 'luxon';
import type { SagaIterator } from 'redux-saga';
import { call, delay, put, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import { v4 as uuid } from 'uuid';

import { a2aTransfersActions } from 'app-components/flux/a2aTransfers';
import { accountsActions, accountsSelectors, accountsTypes } from 'app-components/flux/accounts';
import { resourceSagaUtils } from 'app-components/flux/core';
import {
  type QcsSyncResourcesConfigProps,
  createTypedQcsSyncResourcesConfigFactory,
  mkQcsSyncResourceConfig,
} from 'app-components/flux/core/resourceStoreTypes';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';

import { getEnvironmentConfig, getLogger, httpClientInterface } from 'app-components/utils/core';

import * as actions from './transactionsActions';
import * as transactionsSelectors from './transactionsSelectors'; // , getTransactionById TODO: see performTxnAction note
import * as transformers from './transactionsTransformers';
import type { CashFlowTransaction, TransactionProps } from './transactionsTypes';

const log = getLogger('app-components/flux/transactions/transactionsSagas');

const mkQcsSyncTransactionsConfig = createTypedQcsSyncResourcesConfigFactory<TransactionProps>();

// ====================================================
// PERFORM TRANSACTION ACTION
// a saga for utilizing the transactions/{id}/action or transactions/{id}/action/{otherId}
// pass in an action api object
//
// There is a potential for QCS to auto-match and delete/accept upcoming instances if the user happens to be doing this action
// during a refresh. While we can't completely prevent this from causing errors, we SHOULD check to make sure any respective txns still
// exist client-side before telling QCS to do something with them.
//
// {
//   id: txnId to impact
//   action: 'ACCEPT' || 'SKIP' || 'MATCH' || 'UNMATCH'   See QCS Documentation for updates
//   otherId: if two IDs are involved in action (e.g. match)
// }
//
export function* performTransactionAction(actionData: ReturnType<typeof actions.performTransactionAction>) {
  // TODO: once mobile migrates txns to redux, consider adding this check to prevent match failures and whatnot
  // // make sure firstTxn still exists before performing
  // const firstId = actionData?.payload?.id;
  // if (!firstId) {
  //   return null;
  // }
  // const firstTxn = yield select((store) => getTransactionById(store, firstId));
  // if (!firstTxn) {
  //   return null;
  // }
  //
  // const secondId = actionData?.payload?.otherId;
  // if (secondId) { // if secondId, make sure second txn still exists before performing
  //   const secondTxn = yield select((store) => getTransactionById(store, secondId));
  //   if (!secondTxn) {
  //     return null;
  //   }
  // }

  const config = mkQcsSyncTransactionsConfig({
    resourceName: 'transaction action',
    resourceBaseUrl: (action) =>
      `/transactions/${action.payload.id}/${action.payload.action.toLowerCase()}${
        action.payload.otherId ? `/${action.payload.otherId}` : ''
      }`,
    successAction: actions.performTransactionActionSuccess,
    failureAction: actions.performTransactionActionFailure,
    getLastSyncDate: () => null,
    transformResourceToRequestData: (resource) => {
      const { ...other } = resource;
      return { ...other };
    },
    transformResourceWithResponse: (_resource, response) => {
      if (response?.data?.resources) {
        return transformers.transformQcsTransactionsToTransactions(response.data.resources);
      }
      if (response.data?.resource) {
        return transformers.transformQcsTransactionToTransaction(response.data.resource);
      }
      return null;
    },
  });

  yield call(resourceSagaUtils.qcsSyncUpdateResource<TransactionProps>, config, actionData);
  return null;
}

const { callFirst, callLast } = resourceSagaUtils.createGreedDebounce('transactionsDumpUpdater');
export function* transactionsDumpUpdater(resources: Array<TransactionProps>) {
  yield callFirst(transactionsDumpUpdaterFull, resources);
  yield callLast(transactionsNudgeUpdater);
}

function* transactionsDumpUpdaterFull(resources: Array<TransactionProps>): SagaIterator {
  const process: boolean = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpProcessTxnUpdates');
  if (process) {
    yield put(
      actions.applyTransactionsChanges(transformers.transformQcsTransactionsToTransactions(resources), {
        context: 'BUMP_CHANGE_DUMP',
      }),
    );
  }
}

export function* transactionsNudgeUpdater(): SagaIterator {
  const process = yield select(featureFlagsSelectors.getFeatureFlag, 'bumpProcessTxnUpdates');
  if (process) {
    yield put(actions.getTransactions(undefined, { context: 'BUMP_CHANGE_NUDGE' }));
  }
}

const mkResourceConfig = (props: Partial<QcsSyncResourcesConfigProps<TransactionProps>>) =>
  mkQcsSyncTransactionsConfig({
    resourceName: 'transaction',
    resourceBaseUrl: '/transactions',
    getLastSyncDate: transactionsSelectors.getLastSyncDate,
    transformResourceWithResponse: transformers.transformResourceWithResponse,
    transformResponseToResources: transformers.transformResponseToTransactions,
    transformResourceToRequestData: transformers.transformTransactionToRequestData,
    transformResourcesToRequestData: transformers.transformTransactionsToRequestData,
    ...props,
  });

export function* getTransactionsActionWatcher() {
  let dateOnAfterTmp: string;
  yield takeLeading(actions.getTransactions, function* getTransactions(action): SagaIterator {
    dateOnAfterTmp = action.meta?.props?.dateOnAfter !== undefined ? action.meta.props.dateOnAfter : dateOnAfterTmp;
    const dateOnAfterDB = yield select(transactionsSelectors.getDateOnAfter);
    const dateOnAfterPref = yield select(preferencesV2Selectors.getPreferenceValue, 'dateOnAfter');
    const dateOnAfter = dateOnAfterTmp || dateOnAfterPref;
    let dateOnBefore;
    if (dateOnAfter < dateOnAfterDB) {
      dateOnBefore = DateTime.fromISO(dateOnAfterDB).plus({ days: 1 }).toISODate(); // setup single chunk load
    }
    const resourceConfig = mkResourceConfig({
      successAction: actions.getTransactionsSuccess,
      failureAction: actions.getTransactionsFailure,
      ...((dateOnAfter || dateOnBefore) && { axiosConfig: { params: { dateOnAfter, dateOnBefore } } }),
      ...(dateOnAfter < dateOnAfterDB && { getLastSyncDate: () => null }), // setup single chunk load
    });
    yield call(resourceSagaUtils.qcsSyncGetResources<TransactionProps>, resourceConfig, action);
  });
}

export function* createTransactionActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.createTransactionSuccess,
    failureAction: actions.createTransactionFailure,
  });
  yield takeEvery(actions.createTransaction, function* createTransaction(action): SagaIterator {
    if (action?.payload?.type === 'INVOICE' && !action.payload.accountId) {
      let accountsReceivable = yield select(accountsSelectors.getAccountsReceivable);
      if (!accountsReceivable) {
        yield put(
          accountsActions.createAccount(
            accountsTypes.mkAccountsReceivable({ name: 'Accounts Receivable', clientId: uuid().toUpperCase() }),
          ),
        );
        const result = yield take(accountsActions.createAccountSuccess);
        accountsReceivable = result.payload;
      }
      yield call(resourceSagaUtils.qcsSyncCreateResource<TransactionProps>, resourceConfig, {
        ...action,
        payload: action.payload.set('accountId', accountsReceivable.id),
      });
    } else {
      yield call(resourceSagaUtils.qcsSyncCreateResource<TransactionProps>, resourceConfig, action);
    }
  });
}

export function* updateTransactionActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.updateTransactionSuccess,
    failureAction: actions.updateTransactionFailure,
  });
  yield takeEvery(
    actions.updateTransaction.type,
    resourceSagaUtils.qcsSyncUpdateResource<TransactionProps>,
    resourceConfig,
  );
}

export function* deleteTransactionActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.deleteTransactionSuccess,
    failureAction: actions.deleteTransactionFailure,
  });
  yield takeEvery(
    actions.deleteTransaction.type,
    resourceSagaUtils.qcsSyncDeleteResource<TransactionProps>,
    resourceConfig,
  );
}

export function* batchTransactionsActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.batchTransactionsSuccess,
    failureAction: actions.batchTransactionsFailure,
  });
  yield takeEvery(
    actions.batchTransactions.type,
    resourceSagaUtils.qcsSyncUpdateResources<TransactionProps>,
    resourceConfig,
  );
}

export function* importAppleWalletTransactionsActionWatcher() {
  yield takeEvery(
    actions.importAppleWalletTransactions,
    function* importAppleWalletTransactions(
      action: ReturnType<typeof actions.importAppleWalletTransactions>,
    ): SagaIterator {
      const { payload, meta } = action;

      try {
        const requestUrl = `${httpClientInterface.getServiceUrl()}/pfm-importer/apple-wallet/import`;
        const requestBody = transformers.transformTransactionsToRequestData(payload);
        const { data } = yield call(httpClientInterface.send, 'POST', requestUrl, requestBody);
        if (meta?.resolve && typeof meta.resolve == 'function') {
          yield call(meta.resolve, data);
        }
      } catch (error) {
        if (meta?.reject && typeof meta.reject == 'function') {
          yield call(meta.reject, error);
        }
      }
    },
  );
}

export function* performTransactionActionWatcher() {
  yield takeEvery(actions.performTransactionAction, performTransactionAction);
}

export function* getTransactionsEarliestDateOnActionWatcher() {
  const resourceConfig = mkQcsSyncResourceConfig({
    resourceBaseUrl: '/transactions/earliest-date-on',
    successAction: actions.getTransactionsEarliestDateOnSucess,
    failureAction: actions.getTransactionsEarliestDateOnFailure,
    transformResourceToRequestData: () => ({ accountIds: [] }),
    transformResourceWithResponse: (_resource, response) => response.data,
  });
  // FIXME: figure out what this /transactions/earliest-date-on is doing and see if we can type the resource correctly
  yield takeEvery(
    actions.getTransactionsEarliestDateOn.type,
    resourceSagaUtils.qcsSyncCreateResource<any>,
    resourceConfig,
  );
}

type genericResponse<T> = {
  config: any;
  data: T;
  headers: any;
  request: any;
  status: number;
  statusText: string;
};

export function* initiateA2ATransferActionWatcher() {
  yield takeEvery(
    actions.initiateA2ATransfer.type,
    function* handle(action: ReturnType<typeof actions.initiateA2ATransfer>) {
      try {
        const requestUrl = `${getEnvironmentConfig().services_url}/transactions/submit-transfer`;
        const response: genericResponse<{ resources: TransactionProps[] }> = yield call(
          httpClientInterface.send,
          'PUT',
          requestUrl,
          {
            ...action.payload,
            amount: Number(action.payload?.amount).toFixed(2), // ensure decimal string
          },
        );
        log.log('initiate A2A Transfer response=', response);

        const resources: CashFlowTransaction[] = transformers.transformQcsTransactionsToTransactions(
          response.data?.resources,
        );

        yield put(actions.initiateA2ATransferSuccess(resources));
        yield put(a2aTransfersActions.getA2aTransfers());

        action.meta?.resolve?.(response);

        // give 2 seconds for service to update, then get the aikya-transfer resources
        yield delay(2000);
        yield put(a2aTransfersActions.getA2aTransfers());
      } catch (e: any) {
        log.warn('Error with A2A transfer request\n', e);
        yield put(actions.initiateA2ATransferFailure(e));
        action.meta?.reject?.(e);
      }
    },
  );
}

export default [
  getTransactionsActionWatcher,
  createTransactionActionWatcher,
  updateTransactionActionWatcher,
  deleteTransactionActionWatcher,
  batchTransactionsActionWatcher,
  importAppleWalletTransactionsActionWatcher,
  performTransactionActionWatcher,
  getTransactionsEarliestDateOnActionWatcher,
  initiateA2ATransferActionWatcher,
];
